<template>
	<div class="issuing-container">

		<!-- Cards Introduction Banner -->
		<CardSlashed class="card-link card-link-primary mt-30" v-if="!isEnabledForIssuing">
			<template #left>
				<div class="card-link-title">
					<h2>{{ getString('issuing_cards_title') }}</h2>
                    <p>{{ getString('issuing_cards_explain') }}</p>
					<div class="card-link-actions border-top-divider mt-20">

						<!-- No stripe account -->
						<template v-if="!hasStripeAccount">
							<div class="align-text">
								<h2>{{getString('issuing_cards_no_stripe')}}</h2>
								<p>{{getString('issuing_cards_no_stripe_explain')}}</p>
							</div>
							<AppButton
								:text="getString('stripe_connect_cta')"
								inverted
								@click="goToStripe"
							/>
						</template>

						<!-- Stripe account to migrate -->
						<template v-else-if="hasStripeAccount && !isCustomAccount">
							<div class="align-text">
								<h2>{{getString('issuing_cards_migrate_stripe')}}</h2>
								<p>{{getString('issuing_cards_migrate_stripe_explain')}}</p>
							</div>
							<AppButton
								:text="getString('issuing_cards_cta_migrate')"
								inverted
								@click="goToStripe"
							/>
						</template>

						<!-- Stripe account exists but no issuing capability -->
						<template v-else-if="hasStripeAccount && isCustomAccount && !hasIssuingCapability">
							<div class="align-text">
								<h2>{{getString('issuing_cards_no_capability')}}</h2>
								<p>{{getString('issuing_cards_no_capability_explain')}}</p>
							</div>
							<AppButton
								:text="getString('issuing_cards_cta_request')"
								inverted
								:loading="loading"
								@click="requestIssuingCapability"
							/>
						</template>


					</div>
				</div>
			</template>
			<template #right>
				<div class="wrapper-img">
					<picture>
						<source type="image/webp" srcset="https://img.qromo.io/img/banner-issuing.webp">
						<img src="https://img.qromo.io/img/banner-issuing.png" alt="product image">
					</picture>
				</div>
			</template>
		</CardSlashed>
		
		<!-- Show Cards Interface -->
		<template v-else>
			<CardsBalanceContainer v-if="!cardsStore.showingCards" />
			<CardsManagementContainer v-else />
		</template>
		
		<!-- Issuing Onboarding Bottom Sheet -->
		<AppBottomSheet :active="showIssuingOnboarding" @toggling-popup="toggleIssuingOnboarding" :force-width="bottomSheetWidth">
			<div id="issuing-onboarding-container"></div>
		</AppBottomSheet>
	</div>
</template>

<script>
import CardsManagementContainer from "@/console-new/components/config/administration/cards/CardsManagementContainer.vue";
import CardsBalanceContainer from "@/console-new/components/config/administration/cards/CardsBalanceContainer.vue";
import CardSlashed from "@/console-new/components/shared/CardSlashed.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import {useCardsStore} from "@/console-new/stores/cards.store";
import {useUserStore, useAdministrationStore} from "@/console-new/stores";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import {useRouter} from "vue-router";
import API from "@/shared/helpers/API";
import WindowWidth from "@/console-new/mixins/WindowWidth";

export default {
	setup() {
		const cardsStore = useCardsStore();
		const userStore = useUserStore();
		const administrationStore = useAdministrationStore();
		const router = useRouter();
		return {
			cardsStore,
			userStore,
			administrationStore,
			router,
			getString: useLanguageStore().getString,
			showNotification: useNotificationStore().showNotification
		}
	},
    mixins: [WindowWidth],
	data() {
		return {
			loading: false,
			showIssuingOnboarding: false,
			embeddedOnboardingComponent: null
		}
	},
	computed: {
        bottomSheetWidth() {
            if(this.windowWidth < 550) {
                return "100%";
            } else if(this.windowWidth < 768) {
                return "550px";
            } else if(this.windowWidth < 1080) {
                return "720px";
            } else {
                return "960px";
            }
        },
		hasStripeAccount() {
			return this.userStore.currBusiness && this.userStore.currBusiness.stripe_account;
		},
		isCustomAccount() {
			return this.userStore.currBusiness && this.userStore.currBusiness.stripe_account_type === 'custom';
		},
		hasIssuingCapability() {
			return this.userStore.currBusiness && this.userStore.currBusiness.config_issuing;
		},
        isEnabledForIssuing() {
            return this.hasStripeAccount && this.isCustomAccount && this.hasIssuingCapability;
        }
	},
	methods: {
		goToStripe() {
			this.router.push('/config/administration/stripe');
		},
		requestIssuingCapability() {
			this.loading = true;
			API.init().requestIssuingCapability(this.userStore.currBusinessId, (resp) => {
				if (resp && resp.result === "OK") {
					// Get account session for onboarding
					this.administrationStore.getStripeConnectInstance("onboarding", connectInstance => {
						this.loading = false;
						if (connectInstance) {
							this.showIssuingOnboarding = true;
							const container = document.getElementById('issuing-onboarding-container');
							this.embeddedOnboardingComponent = connectInstance.create("account-onboarding");
							this.embeddedOnboardingComponent.setOnExit(() => {
								this.showIssuingOnboarding = false;
								// Refresh business data to check if issuing is now enabled
								this.userStore.refreshBusiness(this.userStore.currBusinessId, () => {
									// Will automatically show cards interface if issuing is enabled
								});
							});
							container.appendChild(this.embeddedOnboardingComponent);
						} else {
							this.showNotification('generic-fail');
						}
					});
				} else {
					this.loading = false;
					if (resp && resp.details === 'no_stripe_account') {
						this.showNotification('no-stripe-account');
					} else if (resp && resp.details === 'not_custom_account') {
						this.showNotification('custom-account-required');
					} else {
						this.showNotification('generic-fail');
					}
				}
			});
		},
		toggleIssuingOnboarding(active) {
			if (!active) {
				if (this.embeddedOnboardingComponent) {
					const container = document.getElementById('issuing-onboarding-container');
					if (container && container.firstChild) {
						container.removeChild(container.firstChild);
					}
					this.embeddedOnboardingComponent = null;
				}
				this.showIssuingOnboarding = false;
			}
		}
	},
	name: "CardsContainer",
	components: {CardsManagementContainer, CardsBalanceContainer, CardSlashed, AppButton, AppBottomSheet},
}
</script>

<style scoped lang="scss">
.issuing-container {
	max-width: 1400px;
	margin: 0 auto;
}

#issuing-onboarding-container {
	min-height: 30vh;
	padding: 3px 4px;
	overflow: hidden;
	margin: 0 auto;
}

.card-container:deep(.card-body) {
    padding: 0 !important;
    & > .list-container {
        .list-item:first-child::before {
            display: none;
        }
        .list-item:last-child::after {
            display: none;
        }
    }
}

.card-link-primary {
    margin-top: var(--main-padding);
}

.card-link {
    .card-link-title {
        width: 100%;
        display: flex;
        align-items: flex-start;
        justify-content: flex-start;
		flex-direction: column;
		color: var(--color-txt-white);
		max-width: 400px;
		padding: 15px;
		text-align: left;
        .icon-plugins {
            display: inline-block;
            width: 15px;
            height: 15px;
            margin-top: 5px;

            &:deep(svg) {
                width: 15px;
                height: 15px;
                .fill { fill: var(--color-txt-white); }
                .stroke { stroke: var(--color-txt-white); }
            }
        }
        .align-text {
            display:flex;
            flex-direction: column;
        }
        h2 {
            font-size: 22px;
            display: inline-block;
            color: var(--color-txt-white);
            @media (max-width: 550px) {
                font-size: 20px;
                line-height: normal;
            }
        }
        p {
            font-size: 14px;
            line-height: 18px;
            color: var(--color-txt-white);
            margin-top: 5px;
        }
		.card-link-actions {
			display: flex;
			flex-direction: column;
			gap: 4px;
			width: 100%;
			justify-content: flex-start;
			margin-top: 20px;
			padding-top: 20px;

			.align-text {
				h2 {
					font-size: 18px;
					margin: 0 0 5px 0;
				}
				p {
					font-size: 14px;
					margin: 0 0 10px 0;
					opacity: 0.9;
				}
			}

			.base-button {
				height: 40px;
			}
		}
		.card-link-body {
			justify-content: flex-start;

			.card-link-actions {
				width: 100%;
				justify-content: flex-start;
			}
			p {
				width: 100%;
				font-size: 14px;
				line-height: 18px;
				text-align: left;
			}

			@media (max-width: 500px) {
				p {font-size: 12px;}
				.card-link-actions {
					justify-content: flex-end;
				}
			}
		}
        &.no-icon {
            h2 {
                width: 100%;
                padding-left: 0;
            }
        }

        .border-top-divider {
            border-top: 1px solid rgba(255,255,255,.5);
        }
    }
    &-primary {
        &:deep(.slashed-card-container) {
            background: linear-gradient(198deg, #0D8F82 0%, rgb(47, 36, 209) 100%);
            .slashed-left {
                width: 80%;
                justify-content: flex-start;
                .card-link-title {
                    width: 100%;
                    max-width: unset;
                }

                @media (max-width: 550px) {
                    width: 100%;
                    padding-right: 10px;
                    padding-bottom: 10px;
                    padding-top: 0;
                }
            }
            .slashed-right {
                display: flex;
                align-items: center;
                justify-content: center;
                @media (max-width: 550px) {
                    padding-top: 10px;
                    padding-bottom: 0;
                }
            }

            @media (max-width: 550px) {
                flex-direction: column-reverse;
            }
        }
    }
    &-secondary {
        &:deep(.slashed-card-container) {
            background: linear-gradient(198deg, #0D8F82 0%, rgb(47, 36, 209) 100%);
        }
    }
    .wrapper-img {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        picture {
            img {
                width: 100%;
                height: 100%;
                max-height: 220px;
                object-fit: contain;
                @media (max-width: 550px) {
                    max-height: 190px;
                }
            }
        }
    }
}
</style>